import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import {
  addDays,
  addMonths,
  dayOfWeek,
  endOfMonth,
  startOfMonth,
  type IsoDate,
} from '@/features/manufacturing/planning/dates';
import type { CalendarEvent, CalendarEventKind, PlanningModel } from '@/features/manufacturing/planning/types';
import { formatCurrency, formatDateOnly } from '@/lib/formatters';
import { useIsMobile } from '@/lib/useMediaQuery';
import { cn } from '@/lib/utils';
import { ItemPlanSheet } from './ItemPlanSheet';
import { EVENT_KIND_META, EVENT_SEVERITY_RING, monthLabel } from './shared';

interface CalendarTabProps {
  model: PlanningModel;
}

const ALL_KINDS = Object.keys(EVENT_KIND_META) as CalendarEventKind[];
const DEFAULT_KINDS: CalendarEventKind[] = ALL_KINDS.filter((k) => k !== 'planned_receipt');
const WEEK_HEADER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_LONG = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

function EventChip({ event, compact, onClick }: { event: CalendarEvent; compact?: boolean; onClick?: () => void }) {
  const meta = EVENT_KIND_META[event.kind];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      title={event.detail ?? event.title}
      className={cn(
        'flex w-full items-center gap-1 truncate rounded border px-1.5 py-0.5 text-left text-[11px] leading-tight transition-colors hover:brightness-95',
        meta.chip,
        EVENT_SEVERITY_RING[event.severity],
        compact && 'py-px',
      )}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{event.title}</span>
    </button>
  );
}

function EventRow({ event, onOpenItem }: { event: CalendarEvent; onOpenItem: (itemId: string) => void }) {
  const meta = EVENT_KIND_META[event.kind];
  const Icon = meta.icon;
  return (
    <li className="flex gap-3 py-3">
      <span className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border', meta.chip)}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('text-sm font-medium', event.severity === 'critical' && 'text-fresh-salmon-dark')}>{event.title}</span>
          {event.severity === 'critical' && <Badge variant="warning">Act now</Badge>}
          {event.amountCents != null && <span className="text-xs text-muted-foreground tabular-nums">{formatCurrency(event.amountCents)}</span>}
        </div>
        {event.detail && <p className="text-xs text-muted-foreground">{event.detail}</p>}
        <div className="flex flex-wrap gap-3 text-xs">
          {event.itemId && (
            <button type="button" className="inline-flex items-center gap-0.5 text-info hover:underline" onClick={() => onOpenItem(event.itemId!)}>
              Part details <ArrowUpRight className="h-3 w-3" />
            </button>
          )}
          {event.poId && (
            <Link to="/procurement/purchase-orders" className="inline-flex items-center gap-0.5 text-info hover:underline">
              Purchase orders <ArrowUpRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * The procurement calendar: order-by days, last safe days, stock
 * run-outs, PO arrivals and build targets on one month grid. Tap a day
 * for the full list; tap an event to open the part behind it.
 */
export function CalendarTab({ model }: CalendarTabProps) {
  const isMobile = useIsMobile();
  const [monthStart, setMonthStart] = useState<IsoDate>(() => startOfMonth(model.today));
  const [kinds, setKinds] = useState<Set<CalendarEventKind>>(() => new Set(DEFAULT_KINDS));
  const [dayOpen, setDayOpen] = useState<IsoDate | null>(null);
  const [itemOpen, setItemOpen] = useState<string | null>(null);

  const eventsByDate = useMemo(() => {
    const map = new Map<IsoDate, CalendarEvent[]>();
    for (const event of model.events) {
      if (!kinds.has(event.kind)) continue;
      const list = map.get(event.date) ?? [];
      list.push(event);
      map.set(event.date, list);
    }
    return map;
  }, [model.events, kinds]);

  const monthEnd = endOfMonth(monthStart);
  const gridStart = useMemo(() => {
    const dow = dayOfWeek(monthStart);
    return addDays(monthStart, dow === 0 ? -6 : 1 - dow);
  }, [monthStart]);
  const cells = useMemo(() => {
    const out: IsoDate[] = [];
    let d = gridStart;
    while (d <= monthEnd || out.length % 7 !== 0) {
      out.push(d);
      d = addDays(d, 1);
      if (out.length > 42) break;
    }
    return out;
  }, [gridStart, monthEnd]);

  const monthEvents = useMemo(
    () =>
      [...eventsByDate.entries()]
        .filter(([date]) => date >= monthStart && date <= monthEnd)
        .sort((a, b) => a[0].localeCompare(b[0])),
    [eventsByDate, monthStart, monthEnd],
  );

  const todayCritical = (eventsByDate.get(model.today) ?? []).filter((e) => e.severity === 'critical');
  const countOfKind = (kind: CalendarEventKind) => model.events.filter((e) => e.kind === kind && e.date >= monthStart && e.date <= monthEnd).length;

  const toggleKind = (kind: CalendarEventKind) =>
    setKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  const dayEvents = dayOpen ? (eventsByDate.get(dayOpen) ?? []) : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="icon" aria-label="Previous month" onClick={() => setMonthStart((m) => addMonths(m, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button type="button" variant="outline" size="icon" aria-label="Next month" onClick={() => setMonthStart((m) => addMonths(m, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="ml-2 text-lg font-medium">{MONTH_LONG.format(new Date(`${monthStart}T00:00:00Z`))}</h2>
          {monthStart !== startOfMonth(model.today) && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setMonthStart(startOfMonth(model.today))}>
              Today
            </Button>
          )}
          <Link
            to={`/procurement/calendar${monthStart !== startOfMonth(model.today) ? `?month=${monthStart.slice(0, 7)}` : ''}`}
            className="ml-2 inline-flex items-center gap-0.5 text-xs text-info hover:underline"
          >
            Open full calendar <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ALL_KINDS.map((kind) => {
            const meta = EVENT_KIND_META[kind];
            const on = kinds.has(kind);
            const count = countOfKind(kind);
            return (
              <button
                key={kind}
                type="button"
                onClick={() => toggleKind(kind)}
                aria-pressed={on}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                  on ? 'border-border bg-card text-foreground' : 'border-transparent bg-secondary text-muted-foreground line-through',
                )}
              >
                <span className={cn('h-2 w-2 rounded-full', meta.dot)} />
                {meta.label}
                {count > 0 && <span className="tabular-nums text-muted-foreground">{count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {todayCritical.length > 0 && (
        <button
          type="button"
          onClick={() => setDayOpen(model.today)}
          className="flex w-full items-center justify-between gap-3 rounded-lg border border-fresh-salmon/60 bg-fresh-salmon/10 px-4 py-3 text-left text-sm hover:bg-fresh-salmon/15"
        >
          <span>
            <span className="font-medium text-fresh-salmon-dark">
              {todayCritical.length} thing{todayCritical.length === 1 ? '' : 's'} need attention today
            </span>
            <span className="ml-2 text-muted-foreground">
              {todayCritical
                .slice(0, 2)
                .map((e) => e.title)
                .join(' · ')}
              {todayCritical.length > 2 ? ' · …' : ''}
            </span>
          </span>
          <ArrowUpRight className="h-4 w-4 shrink-0 text-fresh-salmon-dark" />
        </button>
      )}

      {isMobile ? (
        <Card>
          <CardContent className="pt-4">
            {monthEvents.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Nothing on the calendar this month.</p>
            ) : (
              <div className="divide-y divide-border">
                {monthEvents.map(([date, events]) => (
                  <div key={date} className="py-3">
                    <div className={cn('text-xs font-medium uppercase tracking-wider', date === model.today ? 'text-info' : 'text-muted-foreground')}>
                      {formatDateOnly(date)}
                      {date === model.today && ' · today'}
                      {!model.calendar.isWorkingDay(date) && ' · off'}
                    </div>
                    <ul className="divide-y divide-border/60">
                      {events.map((event) => (
                        <EventRow key={event.id} event={event} onOpenItem={setItemOpen} />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="grid grid-cols-7 border-b border-border text-center text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {WEEK_HEADER.map((label) => (
                <div key={label} className="py-2">
                  {label}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((date, index) => {
                const inMonth = date >= monthStart && date <= monthEnd;
                const events = eventsByDate.get(date) ?? [];
                const working = model.calendar.isWorkingDay(date);
                const isToday = date === model.today;
                const inHorizon = date >= model.today && date <= model.horizonEnd;
                const visible = events.slice(0, 3);
                return (
                  <div
                    key={date}
                    className={cn(
                      'min-h-[112px] border-b border-r border-border p-1.5 align-top',
                      index % 7 === 6 && 'border-r-0',
                      !inMonth && 'bg-muted/30 text-muted-foreground',
                      inMonth && !working && 'bg-secondary/40',
                      !inHorizon && inMonth && 'opacity-70',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setDayOpen(date)}
                      className={cn(
                        'mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs tabular-nums hover:bg-secondary',
                        isToday && 'bg-info text-linen hover:bg-info',
                      )}
                      aria-label={`Open ${formatDateOnly(date)}`}
                    >
                      {Number(date.slice(8, 10))}
                    </button>
                    <div className="space-y-0.5">
                      {visible.map((event) => (
                        <EventChip key={event.id} event={event} compact onClick={() => setDayOpen(date)} />
                      ))}
                      {events.length > visible.length && (
                        <button type="button" onClick={() => setDayOpen(date)} className="px-1 text-[11px] text-muted-foreground hover:underline">
                          +{events.length - visible.length} more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Sheet open={!!dayOpen} onOpenChange={(open) => !open && setDayOpen(null)}>
        <SheetContent side={isMobile ? 'bottom' : 'right'}>
          {dayOpen && (
            <>
              <SheetHeader>
                <SheetTitle>{formatDateOnly(dayOpen)}</SheetTitle>
                <SheetDescription>
                  {dayOpen === model.today ? 'Today · ' : ''}
                  {model.calendar.isHoliday(dayOpen) ? 'Holiday' : model.calendar.isWorkingDay(dayOpen) ? 'Working day' : 'Non-working day'}
                  {' · '}
                  {dayEvents.length} event{dayEvents.length === 1 ? '' : 's'}
                  {' · '}
                  {monthLabel(dayOpen)}
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                {dayEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing scheduled.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {dayEvents.map((event) => (
                      <EventRow key={event.id} event={event} onOpenItem={setItemOpen} />
                    ))}
                  </ul>
                )}
              </SheetBody>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ItemPlanSheet plan={itemOpen ? (model.itemsById.get(itemOpen) ?? null) : null} model={model} onOpenChange={(open) => !open && setItemOpen(null)} />
    </div>
  );
}
